import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
    Icon, Pagination, TabPane, Tabs,
} from 'ui-library';
import { ICON_PROXY_CONNECTION } from 'ui-library/components/Icon';
import SM from 'services/ServiceManager';
import PageSection from 'components/PageSection/PageSection';
import { withMainLayout } from 'hocs';
import Preloader from 'components/Preloader';
import { KYC } from 'constants/constants';
import { getClientId } from 'utils';
import { IsAllDataNull } from 'utils/validation';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import { useQueries } from '@tanstack/react-query';
import { KYCForm } from 'components/KYCForm';
import { errorModel, getModel, sendModel } from 'components/KYCForm/models';
import { useSelector } from 'react-redux';
import { memberProfileSelector } from 'redux-store/auth/authSelectors';
import { KYCDataPayload, KYCErrorData } from 'components/KYCForm/kyc.types';
import { JaUser } from './types';
import './KYCPage.css';
import { usePortfolioProcessNavigation } from '../../hooks/helpers/usePortfolioProcessNavigation';
import { usePortfolioProcessLayoutActions } from '../../datasource/PortfolioProcessLayout/usePortfolioProcessLayoutActions';
import {
    useCreatePortfolioActions,
    useCreatePortfolioSelectors,
} from '../../datasource/CreatePortfolio';

const buildPatchedContact = (extKyc: Record<string, any>) => (old: any) => (old
    ? {
        ...old,
        data: {
            ...old.data,
            details: {
                ...old.data?.details,
                extKyc: {
                    ...old.data?.details?.extKyc,
                    ...extKyc,
                },
            },
        },
    }
    : old);

const KYCPage = ({ dfsClientId }) => {
    const memberProfile:any = useSelector(memberProfileSelector);
    const [currentTab, setCurrentTab] = useState<number>(0);

    const {
        t,
        i18n: { language },
    } = useTranslation();

    const contactId = getClientId(dfsClientId);
    const { mutateAsync: patchContactMutate } = useGenericAPIMutation();

    const [personalDetails, setPersonalDetails] = useState<any>({});
    const [jaPersonalDetails, setJaPersonalDetails] = useState<Record<
        string,
        any
    > | null>(null);
    const [data, setData] = useState<KYCDataPayload>({});
    const [jaData, setJaData] = useState<Record<string, any> | null>(null);
    const [jaUsers, setJaUsers] = useState<JaUser[]>([]);
    const [dataExists, setDataExists] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [pageErrors, setPageErrors] = useState(undefined);

    const [errorData, setErrorData] = useState<KYCErrorData | null>(null);
    const [jaErrorData, setJaErrorData] = useState<Record<
        string,
        KYCErrorData
    > | null>(null);

    const isSwissNationality = useMemo(() => {
        if (
            personalDetails?.details?.personalInformation?.identification
                ?.length > 0
        ) {
            return (
                personalDetails?.details?.personalInformation?.identification[0]
                    ?.countryOfNationality === KYC.swissNationality
            );
        }

        return false;
    }, [personalDetails?.details?.personalInformation]);

    const {
        setAdditionalComponentLabelAction,
        setOnAdditionalComponentLabelAction,
    } = usePortfolioProcessLayoutActions();

    const {
        isJointAccountSelector: isJA,
        contactGroupIdSelector,
        containerIdSelector,
        is3bProductGroupSelector,
    } = useCreatePortfolioSelectors();

    const { saveInvestmentApplicationDataAction } = useCreatePortfolioActions();

    const { data: threeBActivePortfolioData } = useGetCustomService({
        service: 'customClientService',
        api: 'hasThreeBActivePortfolio',
        payload: { contactId, contactGroupId: contactGroupIdSelector },
        enabled: !!contactId,
    });

    const hasActive3bPortfolio = threeBActivePortfolioData?.data?.hasActivePortfolios ?? false;

    const { data: contactGroupData, isFetching: isUsersLoading } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroupDetails',
        payload: { groupId: contactGroupIdSelector },
        enabled: !!(isJA && contactGroupIdSelector),
    });

    useEffect(() => {
        if (!isJA || jaUsers.length || !contactGroupData?.data?.owners) return;

        const users: JaUser[] = [];

        Object.entries(contactGroupData.data.owners || {})
            .sort((a, b) => +a - +b)
            .forEach(([key, value]) => {
                users.push({
                    id: key,
                    name: value as string,
                    isValid: true, // by default is valid, but after validation is done, it will be set to false if there are errors
                });
            });

        setJaUsers(users);
    }, [isJA, contactGroupData]);

    const {
        data: contactData,
        isFetching: isContactLoading,
    } = useGetCustomService({
        service: 'clientService',
        api: 'getContact',
        payload: contactId,
        enabled: !!contactId && !isJA,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60,
    });

    useEffect(() => {
        const contact = contactData?.data;

        if (isJA || !contact) return;

        setPersonalDetails(contact);
        setData(getModel(contact?.details?.extKyc));
        setDataExists(true);
    }, [contactData, isJA]);

    const jaContactQueries = useQueries({
        queries: (isJA ? jaUsers : []).map((user) => ({
            queryKey: ['getData_getContact', user.id],
            queryFn: () => SM.clientService('getContact', [user.id]),
            enabled: isJA && !!user.id,
            staleTime: 1000 * 60 * 60,
            gcTime: 1000 * 60 * 60,
        })),
    });

    const isJaContactsLoading = jaContactQueries.some((query) => query.isFetching);
    const jaContactsUpdatedKey = jaContactQueries
        .map((query) => query.dataUpdatedAt)
        .join(',');

    useEffect(() => {
        if (
            !isJA
            || !jaUsers.length
            || jaContactQueries.length !== jaUsers.length
            || !jaContactQueries.every((query) => query.isSuccess)
        ) return;

        const jaDetails: Record<string, any> = {};
        const newJaData: Record<string, any> = {};

        jaUsers.forEach((user, index) => {
            const contactDataForm = (jaContactQueries[index]?.data as any)?.data ?? null;

            jaDetails[user.id] = { ...user, ...(contactDataForm || {}) };

            const jaKYCData = getModel(jaDetails[user.id]?.details?.extKyc);

            if (jaKYCData) {
                newJaData[user.id] = jaKYCData;
            } else {
                newJaData[user.id] = { contactId: +user.id };
            }

            jaDetails[user.id].dataExists = !!jaKYCData || false;
        });

        setJaPersonalDetails(jaDetails);
        setJaData(newJaData);
    }, [isJA, jaContactsUpdatedKey]);

    const isContactDetailsLoading = isJA ? isJaContactsLoading : isContactLoading;

    const onJaContinue = useCallback(async () => {
        const err = { ...jaErrorData };

        if (jaData === null) return;

        const jaValidatedUsers = [...jaUsers];

        let isError = false;

        const errorPromises = jaValidatedUsers.map((user) => errorModel(
            jaData[user.id],
            jaData[user.id]?.details?.personalInformation?.identification[0]
                ?.countryOfNationality === KYC.swissNationality,
        ).then((errors) => {
            err[user.id] = errors;
            user.isValid = IsAllDataNull(errors);
            isError = isError || !user.isValid;
        }));

        await Promise.all(errorPromises);

        setJaErrorData(err);

        if (!isError) {
            try {
                setSaveInProgress(true);

                // Map users to an array of promises (Parallel execution)
                const patchPromises = jaUsers.map((usr) => {
                    const payload = sendModel({
                        data: { ...jaData[usr.id], modifiedBy: memberProfile?.Id },
                    });

                    return patchContactMutate({
                        service: 'clientService',
                        API: 'patchContact',
                        payload: [usr?.id, payload],
                        setQueryData: {
                            queryKey: ['getData_getContact', usr.id],
                            queryData: buildPatchedContact(payload.details.extKyc),
                        },
                    });
                });

                // Wait for all patchContact calls to resolve
                const responses = await Promise.all(patchPromises);

                // Check if every response returned a truthy value
                const allWentGood = responses.every((response) => !!response);

                if (!allWentGood) return;

                try {
                    // KYC done api call
                    await saveInvestmentApplicationDataAction({
                        payload: { kycDone: true },
                        method: 'saveKYCDoneByContainer',
                        additionalData: {
                            kycDone: true,
                            id: containerIdSelector,
                        },
                    });
                    stepComplete();
                } catch (ex: any) {
                    setPageErrors(ex?.message);
                }
            } catch (ex: any) {
                setPageErrors(ex.message);
            } finally {
                setSaveInProgress(false);
            }
        }
    }, [jaData, jaPersonalDetails, containerIdSelector, memberProfile]);

    const onContinue = useCallback(async () => {
        if (isJA) {
            onJaContinue();

            return;
        }

        const errors = await errorModel(data, isSwissNationality);

        setErrorData(errors);

        if (IsAllDataNull(errors) !== false) {
            try {
                setSaveInProgress(true);
                const payload = sendModel({ data: { ...data, modifiedBy: memberProfile?.Id } });

                const response = await patchContactMutate({
                    service: 'clientService',
                    API: 'patchContact',
                    payload: [contactId, payload],
                    setQueryData: {
                        queryKey: ['getData_getContact', contactId as number],
                        queryData: buildPatchedContact(payload.details.extKyc),
                    },
                });

                let result;

                if (response) {
                    try {
                        result = await saveInvestmentApplicationDataAction({
                            payload: { kycDone: true },
                            method: 'saveKYCDoneByContainer',
                            additionalData: {
                                kycDone: true,
                                id: containerIdSelector,
                            },
                        });
                    } catch (err: any) {
                        setPageErrors(err?.message);
                    }
                }

                setSaveInProgress(false);
                if (!response || !result) return;
                stepComplete();
            } catch (err: any) {
                setPageErrors(err?.message);
                setSaveInProgress(false);
            }
        }
    }, [
        dfsClientId,
        saveInvestmentApplicationDataAction,
        data,
        contactId,
        dataExists,
        isJA,
        jaData,
        jaPersonalDetails,
        containerIdSelector,
        memberProfile,
    ]);

    const isBeneficialOwner = useMemo(
        () => data?.beneficialOwner === KYC.thirdParty
            || Object.values(jaData || {}).some(
                (s) => s?.beneficialOwner === KYC.thirdParty,
            ),
        [data?.beneficialOwner, jaData],
    );

    const { stepComplete } = usePortfolioProcessNavigation({
        saveOnContinue: onContinue,
        enableAutoStepComplete: false,
        continueDisabled:
            isContactDetailsLoading || saveInProgress || isUsersLoading || isBeneficialOwner,
        continueInProgress: saveInProgress,
        pageErrors,
    });

    const onSkip = useCallback(() => {
        setOnAdditionalComponentLabelAction(undefined);
        stepComplete();
    }, [setOnAdditionalComponentLabelAction]);

    useEffect(() => {
        setAdditionalComponentLabelAction(t('cnpLayout.skipButton'));
    }, [setAdditionalComponentLabelAction, language]);
    useEffect(() => {
        setOnAdditionalComponentLabelAction(
            hasActive3bPortfolio ? onSkip : undefined,
        );
    }, [setOnAdditionalComponentLabelAction, hasActive3bPortfolio, onSkip]);

    useEffect(() => {
        if (
            jaPersonalDetails === null
            || !Object.keys(jaPersonalDetails).length
            || !isJA
        ) return;

        const smallerIndex = Math.min(
            ...Object.keys(jaPersonalDetails).map((id) => +id),
        );

        setCurrentTab(smallerIndex);
    }, [isJA, JSON.stringify(jaPersonalDetails)]);

    useEffect(() => {
        if (jaErrorData === null || !jaUsers.length) return;

        const jaValidatedUsers = [...jaUsers];

        jaValidatedUsers.forEach((usr) => {
            if (jaErrorData[usr.id]) {
                usr.isValid = IsAllDataNull(jaErrorData[usr.id]);
            }
        });
    }, [JSON.stringify(jaErrorData)]);

    return (
        <PageSection className="newStyle">
            <div className="newStyleContent">
                <Preloader isLoading={isContactDetailsLoading || isUsersLoading}>
                    <div className="container">
                        {!isJA && (
                            <KYCForm
                                data={data}
                                personalDetails={personalDetails}
                                setData={setData}
                                errorData={errorData}
                                setErrorData={setErrorData}
                                is3B={is3bProductGroupSelector}
                            />
                        )}

                        {jaUsers.length > 0 && jaData && (
                            <Tabs
                                activeKey={currentTab.toString()}
                                onChange={(key) => setCurrentTab(+key)}
                            >
                                {jaUsers.map((user) => (
                                    <TabPane
                                        tab={(
                                            <div
                                                className={`tab-title ${!IsAllDataNull(jaErrorData?.[user.id] ?? {}) ? 'tab-error' : ''}`}
                                            >
                                                <Icon
                                                    type={
                                                        ICON_PROXY_CONNECTION
                                                    }
                                                    size={24}
                                                />
                                                <span>{user.name}</span>
                                            </div>
                                        )}
                                        key={user.id}
                                    >
                                        <KYCForm
                                            className="ja-form"
                                            key={user.id}
                                            data={
                                                jaData
                                                        && jaData[user.id]
                                                    ? jaData[user.id]
                                                    : {}
                                            }
                                            personalDetails={
                                                jaPersonalDetails
                                                        && jaPersonalDetails?.[
                                                            user.id
                                                        ]
                                                    ? jaPersonalDetails?.[
                                                        user.id
                                                    ]
                                                    : {}
                                            }
                                            setData={(incomingData) => setJaData((prev) => ({
                                                ...prev,
                                                [user.id]: {
                                                    ...incomingData,
                                                },
                                            }))}
                                            errorData={
                                                jaErrorData
                                                    ? jaErrorData[
                                                        user.id
                                                    ]
                                                    : {}
                                            }
                                            setErrorData={(
                                                incomingData,
                                            ) => {
                                                setJaErrorData({
                                                    ...jaErrorData,
                                                    [user.id]: {
                                                        ...incomingData,
                                                    },
                                                });
                                            }}
                                            is3B={
                                                is3bProductGroupSelector
                                            }
                                        />
                                    </TabPane>
                                ))}
                            </Tabs>
                        )}
                    </div>
                    {isJA && jaUsers.length > 0 && (
                        <Pagination
                            className="ja-pagination"
                            total={jaUsers.length}
                            currentPage={
                                jaUsers.findIndex(
                                    (usr) => +usr.id === currentTab,
                                ) + 1
                            }
                            pageSize={1}
                            onChange={(page) => {
                                jaUsers.forEach((usr, index) => {
                                    if (index === page - 1) {
                                        setCurrentTab(+usr.id);
                                    }
                                });
                            }}
                        />
                    )}
                </Preloader>
            </div>
        </PageSection>
    );
};

export default withMainLayout(KYCPage);
